// Typed fetch helpers for /api/multi/*. Mirrors soloApi.ts but every
// route returns a MultiStateResponse so the multi store can refresh
// the lobby roster in lockstep with the player's own shop changes.

import type {
  BuyRequest,
  CombineRequest,
  KickPlayerRequest,
  LockRequest,
  MultiStateResponse,
  ReorderRequest,
  SellRequest,
} from '@origami/shared'
import { type ApiResult } from '../game/playerApi'

async function asResult<T>(res: Response): Promise<ApiResult<T>> {
  if (res.ok) return { ok: true, value: (await res.json()) as T }
  let code = 'unknown'
  let message: string | undefined
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } }
    code = body?.error?.code ?? code
    message = body?.error?.message
  } catch {}
  return { ok: false, code, message, status: res.status }
}

async function post<T>(url: string, body: object = {}): Promise<ApiResult<T>> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return asResult<T>(res)
}

async function postVoid(url: string, body: object = {}): Promise<ApiResult<void>> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (res.ok) return { ok: true, value: undefined }
  let code = 'unknown'
  let message: string | undefined
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } }
    code = body?.error?.code ?? code
    message = body?.error?.message
  } catch {}
  return { ok: false, code, message, status: res.status }
}

export const getMultiSession = async (): Promise<ApiResult<MultiStateResponse>> => {
  const res = await fetch('/api/multi/session', { credentials: 'include' })
  return asResult<MultiStateResponse>(res)
}

export const buyFromMultiShop = (body: BuyRequest) =>
  post<MultiStateResponse>('/api/multi/buy', body)

export const sellFromMultiShop = (body: SellRequest) =>
  post<MultiStateResponse>('/api/multi/sell', body)

export const rerollMultiShop = () => post<MultiStateResponse>('/api/multi/reroll')

export const toggleMultiLock = (body: LockRequest) =>
  post<MultiStateResponse>('/api/multi/lock', body)

export const reorderMultiUnits = (body: ReorderRequest) =>
  post<MultiStateResponse>('/api/multi/reorder', body)

export const combineMultiUnits = (body: CombineRequest) =>
  post<MultiStateResponse>('/api/multi/combine', body)

export const readyForMultiRound = () => post<MultiStateResponse>('/api/multi/ready')

/** POST /api/multi/abandon — drop out of the current multi session
 *  (lobby OR active match). Server cleans up the participant; the
 *  client should clear local store state and route to the hub. */
export const abandonMultiSession = () => postVoid('/api/multi/abandon')

/** POST /api/multi/kick — host marks a stalled / disconnected
 *  participant as ready so the round can resolve. Returns 204 on
 *  success; client triggers `participant_state_update` via the
 *  socket layer (no body to apply). */
export const kickMultiParticipant = (body: KickPlayerRequest) =>
  postVoid('/api/multi/kick', body)
